import { isNavItemVisible } from '@/shared/rbac'
import type { Tour, TourAccessContext } from '../../types'
import { findPageTour, ruleAllows } from '../../utils/eligibility'

/**
 * What each sidebar item is for, keyed by its path (app/navigation.ts). The
 * overview's sidebar-group steps describe ONLY the items the user can see
 * (same `isNavItemVisible` the sidebar uses), in sidebar order. `null` = name
 * the item without a description. A new nav item must be added here (a test
 * fails otherwise).
 */
export const NAV_ITEM_COPY: Record<string, string | null | ((ctx: TourAccessContext) => string)> = {
  // Workspace
  '/workspace/queue': 'holds every chart in flight',
  '/workspace/history': 'keeps exported and archived charts',
  '/workspace/notes': 'collects notes from across your charts',
  // Dashboards
  '/client-admin/overview': 'shows chart counts and pipeline status for your facilities',
  '/performance-dashboard': 'shows coder productivity, coding time and throughput',
  '/batch-runs': 'shows the health of the imports that bring charts in from the EMR',
  '/engine-admin/overview': 'shows how Codio-AI is classifying and coding charts',
  '/billing-impact/summary': 'compares the codes a client billed with what Codio AI predicted',
  // Master Library
  '/kb': 'holds code sets, edits, coverage and guidelines',
  '/platform-config/mandatory-fields': 'lists the chart fields the engine needs before it codes a chart',
  '/prompt-config': 'holds the prompts the coding engine uses',
  '/client-config': 'sets which pipeline modules run and how each client is set up',
  // Clients
  '/clients': 'opens each client\'s record with its facilities, providers and users',
  // My Time / Finance Overview
  '/timesheet': 'holds logged hours for the month',
  '/invoicing/dashboard': 'shows one client\'s billing at a glance',
  '/invoicing/invoices': 'tracks drafts, approvals and outstanding balances',
  '/invoicing/contracts-pricing': 'shows what every client is charged',
  '/invoicing/import-workflows': null,
  '/invoicing/catalog': null,
  // Access
  // Facility requests: only users the server lets file them (access_request_types).
  '/access': (ctx) =>
    ctx.access.canRequestFacilityChange
      ? 'lets you ask for access to another facility or to unmasked patient details, and tracks approvals'
      : 'lets you ask for temporary access to unmasked patient details, and tracks approvals',
  // System
  '/system-admin/clients': 'manages clients, facilities, users and roles',
  '/announcements': 'holds product, guideline and payer updates',
}

/** Page tours, registered by lib/tours/index.ts (avoids an import cycle). */
let pageTours: readonly Tour[] = []
export function registerNavPageTours(tours: readonly Tour[]): void {
  pageTours = tours
}

/**
 * Can the user actually OPEN this item? The sidebar rule is weaker than the
 * route guard for a few items (Prompt Library = edit, Configurations and
 * System Admin = read:all), so an item is described only when the route's own
 * rule (the page tour's copy of it) passes too. Paths without a page tour fall
 * back to the sidebar rule.
 */
export function canOpenNavItem(ctx: TourAccessContext, path: string): boolean {
  const tour = findPageTour(path, pageTours)
  return !tour || ruleAllows(tour.access, ctx)
}

/** The group's items this user can see AND open, in sidebar order. */
export function visibleNavItems(ctx: TourAccessContext, group: string): { label: string; path: string }[] {
  const g = ctx.navGroups.find((x) => x.label === group)
  if (!g) return []
  return g.items
    .filter((it) => isNavItemVisible(it, ctx.access, ctx.has3d) && canOpenNavItem(ctx, it.path ?? ''))
    .map((it) => ({ label: it.label ?? '', path: it.path ?? '' }))
    .filter((it) => it.label)
}

/** `intro` + one clause per visible item: "Chart Queue" holds…; "History" keeps…. */
export function navGroupBody(ctx: TourAccessContext, group: string, intro: string): string {
  const parts = visibleNavItems(ctx, group).map(({ label, path }) => {
    const entry = NAV_ITEM_COPY[path]
    const copy = typeof entry === 'function' ? entry(ctx) : entry
    return copy ? `"${label}" ${copy}` : `"${label}"`
  })
  if (!parts.length) return intro
  return `${intro} ${parts.join('; ')}.`
}
